import { useEffect, useState, type FormEvent } from "react";
import { RefreshCw, Save } from "lucide-react";
import { api } from "../lib/api";
import { displayError } from "../lib/format";
import { notify } from "../lib/notify";
import type { Settings } from "../lib/types";
import { useResource } from "../hooks/useResource";
import { ErrorState, LoadingState, PageHeader } from "../components/Ui";

const defaults: Settings = {
  max_global_active_tasks: 400,
  queue_wait_timeout_seconds: 42000,
  task_timeout_seconds: 2700,
  poll_min_seconds: 3,
  poll_max_seconds: 45,
  task_retention_days: 7,
  request_log_retention_days: 7,
};

export default function SettingsPage() {
  const { data, loading, error, refresh } = useResource((signal) => api.settings(signal), [], undefined);
  const [settings, setSettings] = useState<Settings>(defaults);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setSettings({ ...defaults, ...data }); }, [data]);

  function setNumber(key: keyof Settings, value: string) {
    setSettings((current) => ({ ...current, [key]: Number(value) }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const updated = await api.updateSettings(settings);
      setSettings({ ...settings, ...updated.values });
      notify(updated.restart_required ? "设置已保存，请重启 API 与 worker 使其生效" : "设置已保存", "success");
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader title="系统设置" description="调度容量、租约、素材安全与数据保留" actions={<button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />重新读取</button>} />
      {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : (
        <form className="settings-surface" onSubmit={save}>
          <SettingsSection title="调度容量" description="全局限制仍受有效账号槽位总数约束。">
            <NumberField label="全局在途上限" value={settings.max_global_active_tasks} min={1} max={400} onChange={(value) => setNumber("max_global_active_tasks", value)} />
            <NumberField label="排队超时（秒）" value={settings.queue_wait_timeout_seconds} min={60} max={604800} onChange={(value) => setNumber("queue_wait_timeout_seconds", value)} />
            <NumberField label="生成执行超时（秒）" value={settings.task_timeout_seconds} min={60} max={86400} onChange={(value) => setNumber("task_timeout_seconds", value)} />
          </SettingsSection>
          <SettingsSection title="轮询节奏" description="轮询间隔在最小值和最大值之间按任务进度自适应，并加入 jitter。">
            <NumberField label="最短轮询（秒）" value={settings.poll_min_seconds} min={1} max={30} step={0.5} onChange={(value) => setNumber("poll_min_seconds", value)} />
            <NumberField label="最长轮询（秒）" value={settings.poll_max_seconds} min={3} max={300} step={0.5} onChange={(value) => setNumber("poll_max_seconds", value)} />
          </SettingsSection>
          <SettingsSection title="数据保留" description="维护 worker 分批清理终态任务和脱敏请求日志。">
            <NumberField label="任务保留（天）" value={settings.task_retention_days} min={1} max={3650} onChange={(value) => setNumber("task_retention_days", value)} />
            <NumberField label="请求日志保留（天）" value={settings.request_log_retention_days} min={1} max={3650} onChange={(value) => setNumber("request_log_retention_days", value)} />
          </SettingsSection>
          <div className="settings-actions"><span>保存后需重启 API 与 worker 生效；敏感凭据不会通过此接口返回。</span><button className="button button-primary" type="submit" disabled={saving}><Save size={16} />保存设置</button></div>
        </form>
      )}
    </div>
  );
}

function SettingsSection({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return <section className="settings-section"><header><h3>{title}</h3><p>{description}</p></header><div className="settings-fields">{children}</div></section>;
}

function NumberField({ label, value, min, max, step = 1, onChange }: { label: string; value: unknown; min: number; max: number; step?: number; onChange: (value: string) => void }) {
  return <label className="setting-field"><span>{label}</span><input type="number" value={Number(value ?? 0)} min={min} max={max} step={step} onChange={(event) => onChange(event.target.value)} required /></label>;
}
